import { ref } from 'vue';

/**
 * @typedef {object} TimeoutFnOptions
 * @property {boolean} [immediate=true] - Whether to start the timeout immediately
 * @property {boolean} [immediateCallback=false] - Whether to execute the callback immediately when starting
 */

/**
 * Creates a timeout function with controls for starting, stopping, and checking status
 *
 * @param {Function} fn - The function to execute after the timeout
 * @param {number|import('vue').Ref<number>|Function} interval - The timeout duration in milliseconds
 * @param {TimeoutFnOptions} [options={}] - Configuration options
 */
export function useTimeoutFn(fn, interval, options = {}) {
  const { immediate = true, immediateCallback = false } = options;

  const isPending = ref(false);
  let timer = null;

  function clear() {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function stop() {
    isPending.value = false;
    clear();
  }

  function start(...args) {
    // Get the current interval value (could be ref, getter, or primitive)
    const currentInterval =
      // eslint-disable-next-line no-nested-ternary
      typeof interval === 'function'
        ? interval()
        : interval?.value !== undefined
        ? interval.value
        : interval;

    clear();
    isPending.value = true;

    if (immediateCallback) {
      fn(...args);
    }

    timer = setTimeout(() => {
      isPending.value = false;
      timer = null;
      if (!immediateCallback) {
        fn(...args);
      }
    }, currentInterval);
  }

  if (immediate) {
    isPending.value = true;
    start();
  }

  return {
    isPending,
    start,
    stop,
    clear,
  };
}
